import { useState, useRef, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowLeft, Check, ChevronUp, Filter } from 'lucide-react'
import { supabase } from '../config/supabase'

const heatingAreaRanges = [
  { value: 'all', label: '全部面积' },
  { value: 'small', label: '60-120㎡' },
  { value: 'medium', label: '120-200㎡' },
  { value: 'large', label: '200㎡以上' }
]

export default function WoodFireplaceModelsPage() {
  const [selectedRange, setSelectedRange] = useState('all')
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [showFilter, setShowFilter] = useState(false)
  const [models, setModels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const modelsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchModels = async () => {
      try {
        // 查询产品型号
        const { data: modelsData, error } = await supabase
          .from('product_models_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')
          .order('id', { ascending: true })

        if (error) {
          console.error('Error fetching models:', error)
        } else {
          console.log('Models data from database:', modelsData)
          // 为型号添加默认属性，确保与原代码兼容
          const enhancedModels = (modelsData || []).map((model, index) => ({
            ...model,
            id: model.id,
            name: model.model_name || model.name || `型号${index + 1}`,
            image: model.spec_image || model.image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(model.model_name || model.name || 'wood fireplace model')}&image_size=square`,
            features: Array.isArray(model.features) ? model.features : (typeof model.features === 'string' ? model.features.split(',') : ['经典设计', '真火观赏', '高效燃烧', '安全防护']),
            power: model.rated_power || model.power || '0kW',
            heatingArea: model.heating_area || model.heatingArea || '0-0㎡',
            dimensions: model.dimensions || '0×0×0mm',
            weight: model.weight || '0kg',
            modelNumber: model.model_code || model.modelNumber || '',
          }))
          console.log('Enhanced models:', enhancedModels)
          setModels(enhancedModels)
        }
      } catch (error) {
        console.error('Error fetching models:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchModels()
  }, [])

  // 监听滚动事件，显示/隐藏返回顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 筛选型号
  const filteredModels = models.filter(model => {
    // 型号筛选
    if (selectedModel && model.name !== selectedModel) {
      return false
    }
    
    // 面积筛选
    if (selectedRange === 'small') {
      return model.heatingArea.includes('60-80') || model.heatingArea.includes('80-100') || model.heatingArea.includes('100-120')
    } else if (selectedRange === 'medium') {
      return model.heatingArea.includes('120-140') || model.heatingArea.includes('140-160') || model.heatingArea.includes('160-180') || model.heatingArea.includes('180-200')
    } else if (selectedRange === 'large') {
      return model.heatingArea.includes('200-220') || model.heatingArea.includes('220-250') || model.heatingArea.includes('250-280') || model.heatingArea.includes('280-320')
    }
    
    return true
  })

  // 返回顶部
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <a 
            href="/products" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            返回产品中心
          </a>
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              产品详情
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              燃木壁炉型号
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              多种规格可选，为您的家居增添温暖与氛围
            </p>
          </div>
        </div>
      </section>

      {/* Models Grid */}
      <section className="py-16" ref={modelsRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <h2 className="text-2xl font-bold text-secondary-900">型号列表</h2>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {/* Model Filter */}
              <div className="w-full sm:w-auto">
                <div className="relative">
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    className="flex items-center justify-between w-full sm:w-auto px-4 py-2 bg-secondary-50 rounded-lg border border-secondary-200"
                  >
                    <span className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-secondary-600" />
                      <span>筛选型号</span>
                    </span>
                    <span className="text-secondary-500">
                      {selectedModel || '全部'}
                      <span className="ml-1">▼</span>
                    </span>
                  </button>
                  
                  {/* Filter Options */}
                  {showFilter && (
                    <div className="mt-2 bg-white rounded-lg shadow-lg border border-secondary-200 py-2 w-full sm:w-48 absolute z-10">
                      <button
                        onClick={() => {
                          setSelectedRange('all')
                          setSelectedModel(null)
                          setShowFilter(false)
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-secondary-50 ${selectedModel === null && selectedRange === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-secondary-700'}`}
                      >
                        全部型号
                      </button>
                      {models.map(model => (
                        <button
                          key={model.id}
                          onClick={() => {
                            setSelectedModel(model.name)
                            setShowFilter(false)
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-secondary-50 ${selectedModel === model.name ? 'bg-primary-50 text-primary-700 font-medium' : 'text-secondary-700'}`}
                        >
                          {model.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Area Range Filter */}
              <div className="flex gap-2">
                {heatingAreaRanges.map(range => (
                  <button
                    key={range.value}
                    onClick={() => setSelectedRange(range.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedRange === range.value ? 'bg-primary-600 text-white' : 'bg-secondary-50 text-secondary-700 hover:bg-secondary-100'}`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-secondary-600 text-lg">加载中...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredModels.map((model) => (
                <div key={model.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-secondary-100">
                  <div className="aspect-square overflow-hidden bg-secondary-50">
                    <img
                      src={model.image}
                      alt={model.name}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-secondary-900 mb-1">
                    {model.name}
                  </h3>
                  {model.modelNumber && (
                    <p className="text-xs text-secondary-400 mb-4">
                      型号编号: {model.modelNumber}
                    </p>
                  )}
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">额定功率</span>
                      <span className="font-medium text-secondary-900">{model.power}</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">供暖面积</span>
                      <span className="font-medium text-secondary-900">{model.heatingArea}</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">外形尺寸</span>
                      <span className="font-medium text-secondary-900">{model.dimensions}</span>
                    </div>
                    <div className="flex justify-between pb-2">
                      <span className="text-secondary-600">重量</span>
                      <span className="font-medium text-secondary-900">{model.weight}</span>
                    </div>
                  </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {model.features.map((feature: string, index: number) => (
                        <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                          <Check className="w-3 h-3" />
                          {feature}
                        </span>
                      ))}
                    </div>

                    <a
                      href="/contact"
                      className="block w-full text-center px-4 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                    >
                      咨询购买
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">产品特点</h2>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              燃木壁炉采用经典设计和高效燃烧技术，为您的家居提供温暖的同时增添独特的氛围
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">真火观赏</h3>
              <p className="text-secondary-600">真实火焰效果，营造温馨浪漫的家居氛围</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">高效燃烧</h3>
              <p className="text-secondary-600">采用高效燃烧技术，热效率达到80%以上</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">安全防护</h3>
              <p className="text-secondary-600">多重安全防护设计，使用更放心</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">经典设计</h3>
              <p className="text-secondary-600">经典欧式设计，提升家居品味</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-primary-700 transition-colors z-50"
          aria-label="返回顶部"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
      
      <Footer />
    </div>
  )
}
